import React from "react";
import "../../App.css";
import { NavigationContext } from "../../NavigationContext";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  ValidationModule,
  RowSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  RowStyleModule,
} from "ag-grid-community";
import { API_URL, GRID_COLUMN_EMPTY_ICONS } from "../../utils/Constants";
import PropTypes from "prop-types";
import { ROUTES } from "../../config/routes";
import { colors } from "../../config/colors";
import { SuccessLogo, WarningLogo } from "../../assets/icons";
import { ProductAGgridStyled } from "./styled";
import { Button } from "react-bootstrap";
import { ApiService } from "../../utils/ApiService";
import { PRODUCT_HEADERS } from "config/constants";
import { CustomModal, CUSTOM_MODAL_TYPES, CopyLinkModal } from "../../components";

class ProductAGgrid extends React.Component {
  static contextType = NavigationContext;
  constructor(props) {
    super(props);
    this.adminType = localStorage.getItem("role");
    this.isSuperAdmin = localStorage.getItem("role") === "SUPERADMIN";
    this.isWebCheckoutConfigEnabled = localStorage.getItem("webCheckoutConfig") === "true";
    this.isSchoolAdmin = localStorage.getItem("role") === "SCHOOLADMIN";
    this.onGridReady = this.onGridReady.bind(this);
    this.state = {
      columnDefs: [
        {
          headerName: "Product ID",
          field: "productId",
          icons: GRID_COLUMN_EMPTY_ICONS,
          flex: 1,
        },
        {
          headerName: "Product Name",
          field: "variants",
          sortable: true,
          filter: true,
          icons: GRID_COLUMN_EMPTY_ICONS,
          flex: 2,
          cellRenderer: (params) => params?.data?.variants[0]?.title || "",
        },
        {
          headerName: "Category",
          field: "category",
          sortable: true,
          filter: true,
          cellDataType: "string",
          icons: GRID_COLUMN_EMPTY_ICONS,
          unSortIcon: true,
          flex: 2,
        },
        {
          headerName: "Currency",
          field: "currency",
          sortable: true,
          filter: true,
          icons: GRID_COLUMN_EMPTY_ICONS,
          flex: 1,
        },
        {
          headerName: "Enabled",
          field: "enabled",
          sortable: true,
          filter: true,
          icons: GRID_COLUMN_EMPTY_ICONS,
          flex: 1,
          cellRenderer: (params) => this.renderEnabledIcon(params),
        },
        ...((this.isWebCheckoutConfigEnabled && !this.isSuperAdmin) ? [{
          headerName: "Pay by Link",
          field: "payByLink",
          icons: GRID_COLUMN_EMPTY_ICONS,
          flex: 1.3,
          cellRenderer: (params) => this.renderPayByLinkButton(params),
          onCellClicked: (params) => params.event.stopPropagation(),
        }] : []),
      ],
      paginationPageSize: 10,
      rowData: this.props.studentDetails || [],
      overlayLoadingTemplate:
        '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>',
      overlayNoRowsTemplate:
        '<span class="ag-overlay-no-rows-center">No products found</span>',
      failureMsg: "",
      generatedLink: "",
      showLinkModal: false,
    };
  }

  renderEnabledIcon = (params) => {
    return params.value ? <SuccessLogo /> : <WarningLogo />;
  };

  renderPayByLinkButton = (params) => {
    return (
      <Button
        size="sm"
        variant="outline-primary"
        style={{
          fontSize: "12px",
          padding: "4px 12px",
          borderRadius: "4px",
        }}
        onClick={(e) => {
          e.stopPropagation();
          this.handleGenerateLink(params.data);
        }}
      >
        Pay Link
      </Button>
    );
  };

  handleGenerateLink = async (productData) => {
    try {
      const url = `${API_URL.PRODUCT_DETAILS}${productData?.id}/payment-link`;
      const resp = await ApiService.getRequest(url, PRODUCT_HEADERS);
      if (resp?.status?.code === "Y") {
        const link = resp.body?.link || resp.body?.url || "";
        if (link) {
          this.setState({ 
            showLinkModal: true,
            generatedLink: link 
          });
        } else {
          this.setState({ failureMsg: "No link received from server" });
        }
      } else {
        this.setState({ failureMsg: resp?.status?.message || "Failed to generate link" });
      }
    } catch (err) {
      console.error("Error generating pay by link:", err);
      this.setState({ failureMsg: "Failed to generate link. Please try again." });
    }
  };

  onRowClicked = (params) => {
    // Ignore clicks on Pay by Link column
    if (params.column?.colId === "payByLink" || params.column?.getColId() === "payByLink") {
      return;
    }
    localStorage.setItem("productSelected", params?.data?.id);
    localStorage.setItem(
      "productSelectedName",
      params?.data?.variants[0]?.title
    );
    this.context.navigateTo(`${ROUTES.MARKETPLACE}/${params?.data?.productId}`,
      {...
          this.isSchoolAdmin
           ? { state: { fromMerchantDetails: true } }
           : {}
      }
    );
  };

  onCellClicked = (params) => {
    // Only navigate for non-action columns
    if (params.column?.colId === "payByLink" || params.column?.getColId() === "payByLink") {
      return;
    }
    localStorage.setItem("productSelected", params?.data?.id);
    localStorage.setItem(
      "productSelectedName",
      params?.data?.variants[0]?.title
    );
    this.context.navigateTo(`${ROUTES.MARKETPLACE}/${params?.data?.productId}`,
      {...
          this.isSchoolAdmin
           ? { state: { fromMerchantDetails: true } }
           : {}
      }
    );
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  };

  render() {
    return (
      <ProductAGgridStyled style={{ height: "564px" }}>
        <AgGridReact
          columnDefs={this.state.columnDefs}
          overlayLoadingTemplate={this.state.overlayLoadingTemplate}
          overlayNoRowsTemplate={this.state.overlayNoRowsTemplate}
          rowData={this.state.rowData}
          onGridReady={this.onGridReady}
          rowHeight={51}
          rowStyle={{
            borderBottom: `1px solid ${colors.border}`,
          }}
          onCellClicked={this.onCellClicked}
          modules={[
            ClientSideRowModelModule,
            ValidationModule,
            RowSelectionModule,
            TextFilterModule,
            NumberFilterModule,
            DateFilterModule,
            CustomFilterModule,
            RowStyleModule,
          ]}
        />

        {/* Copy Link Modal */}
        <CopyLinkModal
          show={this.state.showLinkModal}
          onHide={() => {
            this.setState({ showLinkModal: false, generatedLink: "" });
          }}
          title="Link generated successfully!"
          link={this.state.generatedLink}
        />

        {/* Failure Modal */}
        <CustomModal
          show={!!this.state.failureMsg}
          modalType={CUSTOM_MODAL_TYPES.FAILURE}
          onHide={() => {
            this.setState({ failureMsg: "" });
          }}
          title={this.state.failureMsg}
          message=""
        />
      </ProductAGgridStyled>
    );
  }
}

ProductAGgrid.propTypes = {
  studentDetails: PropTypes.array,
};

export default ProductAGgrid;
